// Google Sheets Data Fetcher
// This module fetches member data from a published Google Sheet CSV

// Comprehensive mapping of Turkish country names to English (for react-simple-maps)
// Also includes common variations and alternative spellings
const countryNameMap = {
  // Original mappings
  "Birleşik Krallık": "United Kingdom",
  Türkiye: "Turkey",
  Hollanda: "Netherlands",
  İrlanda: "Ireland",
  Amerika: "United States of America",
  Fransa: "France",
  Almanya: "Germany",
  İsveç: "Sweden",
  Avustralya: "Australia",
  Şili: "Chile",
  Lüksemburg: "Luxembourg",
  İtalya: "Italy",
  Belçika: "Belgium",
  Kıbrıs: "Cyprus",
  // Additional country mappings
  İspanya: "Spain",
  Ispanya: "Spain",
  Portekiz: "Portugal",
  Yunanistan: "Greece",
  Polonya: "Poland",
  Çekya: "Czechia",
  "Çek Cumhuriyeti": "Czechia",
  Avusturya: "Austria",
  İsviçre: "Switzerland",
  Isviçre: "Switzerland",
  Danimarka: "Denmark",
  Norveç: "Norway",
  Finlandiya: "Finland",
  Rusya: "Russia",
  Ukrayna: "Ukraine",
  Japonya: "Japan",
  Çin: "China",
  Hindistan: "India",
  Brezilya: "Brazil",
  Arjantin: "Argentina",
  Meksika: "Mexico",
  Kanada: "Canada",
  "Yeni Zelanda": "New Zealand",
  "Güney Afrika": "South Africa",
  Mısır: "Egypt",
  İsrail: "Israel",
  Israil: "Israel",
  "Birleşik Arap Emirlikleri": "United Arab Emirates",
  BAE: "United Arab Emirates",
  "Suudi Arabistan": "Saudi Arabia",
  Katar: "Qatar",
  Singapur: "Singapore",
  Malezya: "Malaysia",
  Endonezya: "Indonesia",
  "Güney Kore": "South Korea",
  Kore: "South Korea",
  Tayvan: "Taiwan",
  Tayland: "Thailand",
  Vietnam: "Vietnam",
  Filipinler: "Philippines",
  Pakistan: "Pakistan",
  Bangladeş: "Bangladesh",
  İran: "Iran",
  Iran: "Iran",
  Irak: "Iraq",
  Macaristan: "Hungary",
  Romanya: "Romania",
  Bulgaristan: "Bulgaria",
  Hırvatistan: "Croatia",
  Sırbistan: "Serbia",
  Slovenya: "Slovenia",
  Slovakya: "Slovakia",
  Estonya: "Estonia",
  Letonya: "Latvia",
  Litvanya: "Lithuania",
  // Common English names that might appear
  England: "United Kingdom",
  UK: "United Kingdom",
  USA: "United States of America",
  US: "United States of America",
  "United States": "United States of America",
};

/**
 * Normalize a string by converting Turkish special characters to ASCII equivalents
 * This helps match variations like "İspanya" vs "Ispanya"
 */
function normalizeString(str) {
  if (!str) return "";
  return str
    .replace(/İ/g, "I")
    .replace(/ı/g, "i")
    .replace(/Ş/g, "S")
    .replace(/ş/g, "s")
    .replace(/Ğ/g, "G")
    .replace(/ğ/g, "g")
    .replace(/Ü/g, "U")
    .replace(/ü/g, "u")
    .replace(/Ö/g, "O")
    .replace(/ö/g, "o")
    .replace(/Ç/g, "C")
    .replace(/ç/g, "c")
    .trim();
}

/**
 * Build a normalized lookup map for faster matching
 */
const normalizedCountryMap = {};
Object.entries(countryNameMap).forEach(([turkish, english]) => {
  normalizedCountryMap[normalizeString(turkish).toLowerCase()] = english;
});

/**
 * Get English country name from any input (Turkish or English)
 * Handles character variations and common misspellings
 */
function getEnglishCountryName(countryName) {
  if (!countryName) return null;

  const trimmed = countryName.trim();

  // Direct match first (fastest)
  if (countryNameMap[trimmed]) {
    return countryNameMap[trimmed];
  }

  // Normalized match (handles İ/I variations)
  const normalized = normalizeString(trimmed).toLowerCase();
  if (normalizedCountryMap[normalized]) {
    return normalizedCountryMap[normalized];
  }

  // If it's already an English name in our values, return it
  if (Object.values(countryNameMap).includes(trimmed)) {
    return trimmed;
  }

  // Return original if no mapping found (will still show on map if it matches geo data)
  console.warn(`Unknown country name: "${countryName}" - no mapping found`);
  return trimmed;
}

// Published Google Sheet CSV URL (single column of countries)
const GOOGLE_SHEETS_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vSk-yQJ0ZkBJA3fzZgFLlza8iEUrRTprMjkvz5RZL9a2SVUu57jTVQBuzUFhRS0y0mNgYl851uNnLYH/pub?gid=0&single=true&output=csv";

// Cache for storing fetched data
let cachedData = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

/**
 * Fetch country list from Google Sheets (single column CSV)
 * @returns {Promise<string[]>} Array of country names
 */
export async function fetchCountryData() {
  // Check cache first
  if (
    cachedData &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return cachedData;
  }

  try {
    const response = await fetch(GOOGLE_SHEETS_CSV_URL);
    if (!response.ok) {
      throw new Error("Failed to fetch Google Sheets data");
    }

    const csvText = await response.text();

    // Parse single column CSV - split by newlines and clean up
    const allLines = csvText
      .split("\n")
      .map((line) => line.trim().replace(/^"|"$/g, "")) // Remove quotes and whitespace
      .filter((line) => line.length > 0); // Remove empty lines

    // Skip the first row if it's a header (check if it's a known country name)
    const firstLine = allLines[0];
    const isHeader =
      !countryNameMap[firstLine] &&
      !Object.values(countryNameMap).includes(firstLine);

    const countries = isHeader ? allLines.slice(1) : allLines;

    // Update cache
    cachedData = countries;
    cacheTimestamp = Date.now();

    // console.log('Fetched countries:', countries.length, 'unique:', [...new Set(countries.map(c => countryNameMap[c] || c))].length);

    return countries;
  } catch (error) {
    console.error("Error fetching Google Sheets data:", error);
    // Return cached data if available, even if expired
    if (cachedData) {
      return cachedData;
    }
    throw error;
  }
}

/**
 * Get member counts by country from Google Sheets
 * @returns {Promise<Object>} Object with country names (English) as keys and counts as values
 */
export async function getMemberCountsFromSheets() {
  const countries = await fetchCountryData();
  const counts = {};

  countries.forEach((countryInput) => {
    if (countryInput) {
      const englishCountry = getEnglishCountryName(countryInput);
      if (englishCountry) {
        counts[englishCountry] = (counts[englishCountry] || 0) + 1;
      }
    }
  });

  return counts;
}

/**
 * Get total member count from Google Sheets
 */
export async function getTotalMembersFromSheets() {
  const countries = await fetchCountryData();
  return countries.length;
}

/**
 * Get total number of countries from Google Sheets
 */
export async function getTotalCountriesFromSheets() {
  const counts = await getMemberCountsFromSheets();
  return Object.keys(counts).length;
}


